import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { Size } from "@repo/ui/helpers/helpers";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import type { FC } from "react";
import { useMatch, useNavigate, useParams } from "react-router";
import { useUpdateTeammateMutation } from "../api/entities/teammate.js";
import { appPaths } from "../app/appPaths.js";
import { selectTeammate } from "../features/teammate/teammateSlice.js";
import { useAppSelector } from "../hooks/hook.js";
import {
  checkIsVoiceAgentOption,
  usePlaybookAndVoiceAgentOptions,
  type PlaybookAndVoiceAgentOption,
} from "../hooks/usePlaybookAndVoiceAgentOptions.js";

export interface PerformancePlaybookSwitcherProps {
  staysOnFullPath?: boolean;
  /**
   * When provided, restricts the dropdown to this subset of playbooks —
   * voice agents included, matched by their backing playbook id (the
   * currently-selected playbook is always kept so the trigger reflects the
   * URL). Used by the teammate profile to offer only the playbooks the
   * teammate has calls on, and by the signal drilldown to offer only the
   * signal's scope. Omit to offer every playbook.
   */
  selectablePlaybookIds?: readonly PlaybookId[];
  /**
   * Also offer voice agents, grouped under "Playbook" / "Voice agent"
   * headers. A voice agent navigates via the playbook backing the agent.
   * Used by pages whose data exists for agent-backed playbooks (signals).
   */
  shouldIncludeVoiceAgents?: boolean;
}

export const PerformancePlaybookSwitcher: FC<
  PerformancePlaybookSwitcherProps
> = (props) => {
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId URL param");
  }

  const playbookId = asPlaybookId(playbookIdParam);

  const navigate = useNavigate();
  const playbookTabMatch = useMatch(`${appPaths.playbook.pattern}/:tab/*`);

  const { options: companyOptions, isLoading: areOptionsLoading } =
    usePlaybookAndVoiceAgentOptions({
      shouldSkipVoiceAgents: !props.shouldIncludeVoiceAgents,
    });

  const teammate = useAppSelector(selectTeammate);
  const [updateTeammate] = useUpdateTeammateMutation();

  // Optional caller-supplied allowlist (e.g. the playbooks a teammate has
  // calls on). The currently-selected playbook is always kept regardless, so
  // the trigger renders correctly and the unknown-playbook detection below
  // stays keyed off the full known set rather than the narrowed subset.
  const selectablePlaybookIdSet = props.selectablePlaybookIds
    ? new Set<PlaybookId>(props.selectablePlaybookIds)
    : undefined;

  // Build the playbook dropdown options
  const knownOptions: readonly PlaybookAndVoiceAgentOption[] =
    companyOptions.filter(
      (option) =>
        selectablePlaybookIdSet === undefined ||
        selectablePlaybookIdSet.has(option.value) ||
        option.value === playbookId,
    );

  // If the playbook id from the URL doesn't resolve to a known playbook or
  // voice agent, surface a synthetic "Unknown playbook" option so the
  // dropdown can still reflect the current selection (and flag it as invalid
  // below).
  const isUnknownPlaybook =
    !areOptionsLoading && !knownOptions.some((o) => o.value === playbookId);
  const unknownOptions: readonly SimpleSelectOption<PlaybookId>[] =
    isUnknownPlaybook ? [{ label: "Unknown playbook", value: playbookId }] : [];

  const allOptions: readonly SimpleSelectOption<PlaybookId>[] = [
    ...knownOptions,
    ...unknownOptions,
  ];
  // Sections keep their headers even when only one kind survives the
  // allowlist, so a scope-restricted menu still reads as Playbook/Voice agent.
  const knownPlaybookOptions = knownOptions.filter(
    (option) => !checkIsVoiceAgentOption(option),
  );
  const knownVoiceAgentOptions = knownOptions.filter(checkIsVoiceAgentOption);
  const sections = props.shouldIncludeVoiceAgents
    ? [
        ...(knownPlaybookOptions.length > 0
          ? [
              {
                id: "playbooks",
                label: "Playbook",
                options: knownPlaybookOptions,
              },
            ]
          : []),
        ...(knownVoiceAgentOptions.length > 0
          ? [
              {
                id: "voice-agents",
                label: "Voice agent",
                options: knownVoiceAgentOptions,
              },
            ]
          : []),
        ...(unknownOptions.length > 0
          ? [{ id: "unknown", options: unknownOptions }]
          : []),
      ]
    : undefined;

  // Current selected playbook is the one from the path
  const selectedPlaybookOption = allOptions.find(
    (o) => o.value === playbookId,
  )?.value;

  const onPlaybookChange = (value: PlaybookId) => {
    let to = appPaths.playbook.build({ playbookId: value });

    if (playbookTabMatch) {
      to += `/${playbookTabMatch.params.tab}`;

      if (props.staysOnFullPath && playbookTabMatch.params["*"]) {
        to += `/${playbookTabMatch.params["*"]}`;
      }
    }

    navigate(to, { replace: true });

    // Persist the selection so it is restored on the teammate's next visit.
    // Best effort — a failed save just falls back to the default playbook.
    // Voice-agent playbooks are excluded: the preference seeds pages that
    // only handle coaching playbooks.
    const selectedOption = companyOptions.find((o) => o.value === value);
    if (selectedOption && checkIsVoiceAgentOption(selectedOption)) {
      return;
    }
    if (teammate) {
      updateTeammate({
        id: teammate.id,
        patchPayload: {
          preferences: { team_performance_playbook_id: value },
        },
      });
    }
  };

  return allOptions.length > 0 ? (
    <SimpleSelect
      label="Playbook"
      isLabelHidden
      isInvalid={isUnknownPlaybook}
      value={selectedPlaybookOption ?? null}
      options={sections ? undefined : allOptions}
      sections={sections}
      onChange={onPlaybookChange}
      size={Size.SMALL}
    />
  ) : null;
};
